import React, { useCallback, useEffect, useMemo, useState } from "react";
import SidebarMenu from "../../Components/Sidebar/Sidebar";
import { Row, Col, Button } from "react-bootstrap";
import { Input, Table } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchPatientSearch } from "../../Components/Helpers/API/PatientApi/PatientApi";
import "../../Components/Commons/Commons.css";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

// Helper function to calculate age in months from birthdate
const getAgeInMonths = (birthdate) => {
  if (!birthdate) return 0;
  const today = new Date();
  const birth = new Date(birthdate);
  const months =
    (today.getFullYear() - birth.getFullYear()) * 12 +
    (today.getMonth() - birth.getMonth());
  return months;
};

function ConsultPatientSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const queueInfo = useMemo(() => {
    const st = (location && location.state) || {};
    if (st?.queueId && st?.queueNo) {
      return { id: st.queueId, queue_no: st.queueNo };
    }
    try {
      const fromLocal = JSON.parse(localStorage.getItem("queue_id") || "null");
      if (fromLocal?.id && fromLocal?.queue_no) return fromLocal;
    } catch (e) {}
    return { id: "", queue_no: "" };
  }, [location]);

  async function runSearch(nextPage = 1, keyword = "") {
    setLoading(true);
    try {
      const resp = await fetchPatientSearch({
        id: "",
        page: nextPage,
        search: keyword,
      });
      if (resp?.data) {
        setPatients(resp.data.data || []);
        setTotal(resp.data.total_size || 0);
        setPage(nextPage);
      } else {
        setPatients([]);
        setTotal(0);
      }
    } catch (e) {
      setPatients([]);
      setTotal(0);
    }
    setLoading(false);
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => runSearch(1, nextValue), 500),
    []
  );

  useEffect(() => {
    if (search.length >= 3) {
      debouncedSearch(search);
    } else {
      setPatients([]);
      setTotal(0);
      setPage(1);
    }
  }, [search, debouncedSearch]);

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) =>
        `${r.last_name || ""}, ${r.first_name || ""} ${
          r.middle_name || ""
        }`.trim(),
    },
    {
      title: "Sex",
      dataIndex: "sex",
      key: "sex",
      width: 90,
    },
    {
      title: "Birthdate",
      dataIndex: "birthdate",
      key: "birthdate",
      width: 140,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, r) => {
        const handleSelect = () => {
          navigate(`/patients/vitals/${r.id}`, {
            state: {
              fromConsult: true,
              queueId: queueInfo.id,
              queueNo: queueInfo.queue_no,
              patient_sex: r.sex?.toUpperCase() || "",
              months: getAgeInMonths(r.birthdate),
            },
          });
        };
        return (
          <Button className="action-btn save" size="sm" onClick={handleSelect}>
            Select
          </Button>
        );
      },
    },
  ];

  return (
    <div className="App">
      <SidebarMenu active={"Patients"} />
      <div className="col pt-4 scrollable-container">
        <Row className="px-5 mb-4">
          <Col className="col font-big title-2 all-patient all-patients-title">
            Select Patient for Consultation
          </Col>
        </Row>
        <div className="row ms-5">
          <div className="col-5 pe-4 me-2 mb-2 half-width">
            <Input
              placeholder="Search by name"
              allowClear
              size="middle"
              className="responsive-header-input"
              prefix={<FontAwesomeIcon icon={faSearch} style={{ marginRight: 8 }} />}
              onChange={(e) => {
                const v = e.target.value;
                setSearch(v);
              }}
            />
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-sm-11 all-table custom-table-styling">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={patients}
              loading={loading}
              pagination={{
                current: page,
                total: total,
                pageSize: 10,
                onChange: (p) => {
                  runSearch(p, search);
                },
                showSizeChanger: false,
              }}
              locale={{
                emptyText: (
                  <div className="d-flex flex-column align-items-center">
                    <em className="text-muted my-5 text-center">
                      Please search for a patient by <strong>last name</strong>,{" "}
                      <strong>first name</strong>, or <strong>middle name</strong>{" "}
                      to display the list of patients.
                    </em>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsultPatientSearch;
